import React, { useState } from 'react';
import { Form, Input, Button, Typography, Alert, Space } from 'antd';
import { MailOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const _containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'radial-gradient(circle at 20% 0%, rgba(99,102,241,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(14,165,233,0.3) 0%, transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  padding: 24,
  position: 'relative',
  overflow: 'hidden',
};

const _cardStyle: React.CSSProperties = {
  width: 420,
  borderRadius: 18,
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 20px 60px rgba(15,23,42,0.45)',
  padding: 40,
  position: 'relative',
  backdropFilter: 'blur(12px)',
};

export function ForgotPasswordPage(): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async ({ email }: { email: string }): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await axios.post('/api/v1/auth/password/forgot', { email });
      setSent(true);
    } catch (err) {
      // 503 = globaler Mail-Fehler (SMTP nicht konfiguriert o. ä.).
      // Formular bleibt sichtbar, damit der Nutzer den Support kontaktieren kann.
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        setError('E-Mail-Versand ist derzeit nicht verfügbar. Bitte kontaktiere den Support.');
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        const retryAfter = err.response.headers['retry-after'];
        const seconds = retryAfter ? parseInt(retryAfter, 10) : 60;
        setError(
          `Zu viele Anfragen. Bitte warte ${seconds} Sekunden und versuche es erneut.`
        );
      } else {
        // Alle anderen Fehler (Netzwerk, unbekannt) → generische Erfolgs-Meldung
        // behalten, damit Account-Enumeration verhindert wird.
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={_containerStyle}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }}
      />
      <div style={_cardStyle}>
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 12px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 28,
                boxShadow: '0 10px 30px rgba(99,102,241,0.4)',
              }}
            >
              <ThunderboltOutlined />
            </div>
            <Title level={3} style={{ marginBottom: 4 }}>
              Passwort vergessen
            </Title>
            <Text type='secondary'>
              Gib deine E-Mail-Adresse ein. Falls ein Account existiert, erhältst du einen Reset-Link.
            </Text>
          </div>

          {error && (
            <Alert message={error} type='error' showIcon closable onClose={() => setError(null)} style={{ borderRadius: 10 }} />
          )}

          {sent ? (
            <Alert
              message='Wenn ein Account mit dieser E-Mail existiert, wurde ein Reset-Link versendet. Bitte prüfe dein Postfach.'
              type='success'
              showIcon
              style={{ borderRadius: 10 }}
            />
          ) : (
            <Form layout='vertical' onFinish={onFinish} autoComplete='off' size='large'>
              <Form.Item
                name='email'
                label='E-Mail'
                rules={[
                  { required: true, message: 'E-Mail eingeben' },
                  { type: 'email', message: 'Ungültige E-Mail' },
                ]}
              >
                <Input prefix={<MailOutlined style={{ color: 'rgba(15,23,42,0.4)' }} />} placeholder='deine@email.de' />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  block
                  size='large'
                  style={{
                    height: 44,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
                  }}
                >
                  Reset-Link anfordern
                </Button>
              </Form.Item>
            </Form>
          )}

          <div style={{ textAlign: 'center' }}>
            <Link to='/login' style={{ color: '#6366f1', fontSize: 14 }}>
              ← Zurück zur Anmeldung
            </Link>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              © {new Date().getFullYear()} Schnitzraum · sicher und DSGVO-konform
            </Text>
          </div>
        </Space>
      </div>
    </div>
  );
}
